import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faTrash } from '@fortawesome/pro-duotone-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { Select, Store } from '@ngxs/store';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { GridsterComponentInterface, GridsterConfig } from 'angular-gridster2';
import _ from 'lodash';
import { merge, Observable, Subject } from 'rxjs';
import { delay, map, takeUntil, tap } from 'rxjs/operators';
import { GridsterService } from 'src/app/services/gridster/gridster.service';
import { BrickLayoutType } from '../../../../../samwebDescription/samweb/dashboard/brickLayoutType';
import { BrickItemChangedOnWallAction } from '../../../../../store/dashboard/brick-wall/brick-instances/brick-instances.actions';
import {
   ChangeBrickDefinitionMenuOpeningStateForInnerWallBrick,
   RemoveBrickFromInnerWallBrick,
   ToggleBrickDefinitionMenuOpeningStateForInnerWallBrick,
} from '../../../../../store/dashboard/brick-wall/brick-settings/specific-brick-settings/inner-wall-brick-settings/inner-wall-brick-settings.actions';
import { InnerWallBrickSettingsState } from '../../../../../store/dashboard/brick-wall/brick-settings/specific-brick-settings/inner-wall-brick-settings/inner-wall-brick-settings.state';
import { BrickWallState } from '../../../../../store/dashboard/brick-wall/brick-wall.state';
import { ISpecificBrickSettingsComponent } from '../../../../dashboard/brick-settings/brick-specific-type-settings/ISpecificBrickSettingsComponent';
import { gridDefaultValueForInnerWallBrick } from '../../../../dashboard/brick-wall/data/grid-default-values';

@Component({
   selector: 'samweb-inner-wall-brick-settings',
   templateUrl: './inner-wall-brick-settings.component.html',
   styleUrls: ['./inner-wall-brick-settings.component.scss'],
})
export class InnerWallBrickSettingsComponent implements OnInit, AfterViewInit, OnDestroy, ISpecificBrickSettingsComponent {
   public brickInstanceId: string;

   public addBrickIcon: IconDefinition = faPlus;
   public removeBrickIcon: IconDefinition = faTrash;

   @ViewChild('brickSelectionTooltipDirective')
   public brickSelectionTooltipDirective: TooltipDirective;

   @ViewChild('brickSelectionPopupControl', { read: ElementRef })
   public brickSelectionPopupControl: ElementRef;

   @Select(InnerWallBrickSettingsState.getIsBrickDefinitionMenuOpen)
   public isBrickDefinitionMenuOpen$: Observable<boolean>;

   public options: GridsterConfig = gridDefaultValueForInnerWallBrick();

   public brickInstanceIds$: Observable<string[]>;

   private destroy$ = new Subject();

   constructor(private store: Store, private gridsterService: GridsterService) {
      this.options.initCallback = (gridster: GridsterComponentInterface) => {
         this.gridsterService.register(this.brickInstanceId, gridster);
      };
      this.options.destroyCallback = (gridster: GridsterComponentInterface) => {
         this.gridsterService.unregister(this.brickInstanceId);
      };
   }

   ngOnInit(): void {
      this.brickInstanceIds$ = this.store.select(InnerWallBrickSettingsState.brickInstanceIdsForInnerWallBrickFn).pipe(map((fn) => fn(this.brickInstanceId)));

      merge(this.store.select(BrickWallState))
         .pipe(delay(10), takeUntil(this.destroy$))
         .subscribe(() => {
            if (_.isNil(this.options.api)) {
               return;
            }
            this.options.api.optionsChanged();
         });
   }

   ngAfterViewInit(): void {
      this.isBrickDefinitionMenuOpen$
         .pipe(
            tap((isOpen) => {
               if (_.isNil(this.brickSelectionTooltipDirective)) {
                  return;
               }
               if (!isOpen) {
                  this.brickSelectionTooltipDirective.hide();
                  return;
               }
               if (!_.isNil(this.brickSelectionPopupControl)) {
                  this.brickSelectionTooltipDirective.toggle(this.brickSelectionPopupControl.nativeElement, isOpen);
                  return;
               }
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.options.itemChangeCallback = (item, itemComponent) => {
         const brickId = (item as any).brickInstanceId;
         this.store.dispatch(new BrickItemChangedOnWallAction(brickId, item, BrickLayoutType.IN_LAYOUT_BRICK));
      };
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   toggleBrickSelectionDialog() {
      this.store.dispatch(new ToggleBrickDefinitionMenuOpeningStateForInnerWallBrick(this.brickInstanceId));
   }

   closeBrickSelectionDialog() {
      this.store.dispatch(new ChangeBrickDefinitionMenuOpeningStateForInnerWallBrick(this.brickInstanceId, false));
   }

   removeBrickFromInnerWall(brickId: string) {
      this.store.dispatch(new RemoveBrickFromInnerWallBrick(this.brickInstanceId, brickId));
   }
}
